import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  getDocs,
  serverTimestamp,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { 
  Notification, 
  NotificationType, 
  NotificationCategory, 
  NotificationStatus, 
  NotificationChannel, 
  NotificationImportance,
  isValidNotificationType,
  isValidNotificationCategory,
  NotificationCount,
  isValidNotificationStatus,
  isValidNotificationChannel,
  isValidNotificationImportance
} from '../../types/notification';
import { NotificationMetadata, FirestoreDocumentData } from '../../types/common';
import { pushNotificationService } from '../notifications/push-notification.service';
import { getAuth } from 'firebase/auth';

class NotificationService {
  private readonly collectionName = 'notifications';

  async getNotifications(weddingId: string): Promise<Notification[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('weddingId', '==', weddingId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => this.convertToNotification(doc));
    } catch (error) {
      console.error('[DEBUG] Error fetching notifications:', error);
      return [];
    }
  }

  private convertToNotification(doc: DocumentData): Notification {
    try {
      const data = doc.data() || {};
      
      const createdAt = data.createdAt;
      let date: Date;

      if (!createdAt) {
        date = new Date();
        console.warn(`[DEBUG] Missing date for notification ${doc.id}, using current date`);
      } else if (createdAt instanceof Timestamp) {
        date = createdAt.toDate();
      } else if (createdAt instanceof Date) {
        date = createdAt;
      } else if (typeof createdAt.toDate === 'function') {
        try {
          date = createdAt.toDate();
        } catch (error) {
          console.warn(`[DEBUG] Error converting date for notification ${doc.id}:`, error);
          date = new Date();
        }
      } else {
        date = new Date();
        console.warn(`[DEBUG] Invalid date format for notification ${doc.id}, using current date`);
      }

      const notification: Notification = {
        id: doc.id,
        weddingId: data.weddingId || '',
        userId: data.userId || '',
        title: data.title || 'Notification',
        message: data.message || '',
        type: this.validateNotificationType(data.type),
        category: this.validateNotificationCategory(data.category),
        isRead: Boolean(data.isRead),
        createdAt: date,
        link: data.link || undefined,
        status: this.validateNotificationStatus(data.status),
        channel: this.validateNotificationChannel(data.channel),
        importance: this.validateNotificationImportance(data.importance),
        metadata: this.validateMetadata(data.metadata)
      };

      return notification;
    } catch (error) {
      console.error('[DEBUG] Error converting notification:', error);
      throw error;
    }
  }

  private validateNotificationType(type: unknown): NotificationType {
    if (isValidNotificationType(type)) {
      return type;
    }
    return 'info';
  }

  private validateNotificationCategory(category: unknown): NotificationCategory {
    if (isValidNotificationCategory(category)) {
      return category;
    }
    return 'general';
  }

  private validateNotificationStatus(status: unknown): NotificationStatus {
    if (isValidNotificationStatus(status)) {
      return status;
    }
    return 'pending';
  }

  private validateNotificationChannel(channel: unknown): NotificationChannel | undefined {
    if (isValidNotificationChannel(channel)) {
      return channel;
    }
    return undefined;
  }

  private validateNotificationImportance(importance: unknown): NotificationImportance {
    if (isValidNotificationImportance(importance)) {
      return importance;
    }
    return 'medium';
  }

  private validateMetadata(metadata: unknown): NotificationMetadata {
    if (metadata && typeof metadata === 'object' && metadata !== null) {
      const validatedMetadata: NotificationMetadata = {};
      const obj = metadata as Record<string, unknown>;
      
      // Valider chaque propriété
      if (typeof obj['taskId'] === 'string') validatedMetadata.taskId = obj['taskId'];
      if (typeof obj['guestId'] === 'string') validatedMetadata.guestId = obj['guestId'];
      if (typeof obj['budgetId'] === 'string') validatedMetadata.budgetId = obj['budgetId'];
      if (typeof obj['vendorId'] === 'string') validatedMetadata.vendorId = obj['vendorId'];
      if (typeof obj['timelineEventId'] === 'string') validatedMetadata.timelineEventId = obj['timelineEventId'];
      if (typeof obj['achievementId'] === 'string') validatedMetadata.achievementId = obj['achievementId'];
      if (typeof obj['invitationId'] === 'string') validatedMetadata.invitationId = obj['invitationId'];
      
      // Ajouter d'autres propriétés de type primitif
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          validatedMetadata[key] = value;
        }
      });
      
      return validatedMetadata;
    }
    return {};
  }

  subscribeToNotifications(weddingId: string, callback: (notifications: Notification[]) => void) {
    try {
      // Vérifier si l'utilisateur est authentifié
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        console.warn('[DEBUG] Tentative d\'accès aux notifications sans authentification');
        callback([]);
        return () => {};
      }

      const q = query(
        collection(db, this.collectionName),
        where('weddingId', '==', weddingId),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const notifications = snapshot.docs.map(doc => this.convertToNotification(doc));
        callback(notifications);
      }, (error) => {
        console.error('[DEBUG] Error in notifications subscription:', error);
        
        // Gérer spécifiquement les erreurs de permission
        if (error.code === 'permission-denied') {
          console.warn('[DEBUG] Permission denied for notifications. Please check Firebase rules.');
        }
        
        callback([]);
      });
    } catch (error) {
      console.error('[DEBUG] Error setting up notifications subscription:', error);
      return () => {};
    }
  }

  async createNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>): Promise<string> {
    try {
      // Valider que weddingId est présent
      if (!notification.weddingId) {
        console.error('[DEBUG] Tentative de création d\'une notification sans weddingId');
        throw new Error('weddingId is required for notifications');
      }

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...notification,
        isRead: false,
        read: false,
        createdAt: serverTimestamp(),
        status: notification.status || 'pending',
        importance: notification.importance || 'medium',
        category: notification.category || 'general'
      });


      // Envoyer une notification push si possible
      try {
        // Vérifier si les notifications push sont activées et si un userId est spécifié
        if (notification.userId) {
          // Vérifier si nous sommes en mode de repli
          if (pushNotificationService.isFallbackMode()) {
            
            // Vérifier si l'utilisateur a activé les notifications
            const userPrefQuery = query(
              collection(db, 'user_preferences'),
              where('__name__', '==', notification.userId)
            );
            
            const userPrefSnapshot = await getDocs(userPrefQuery);
            if (!userPrefSnapshot.empty && userPrefSnapshot.docs[0].data()['pushNotificationsEnabled']) {
              // En mode de repli, la notification est déjà stockée dans Firestore
              // et sera affichée via l'interface utilisateur
            }
          } else {
            // Essayer de configurer les notifications push
            const pushEnabled = await pushNotificationService.setupPushNotifications();
            if (pushEnabled) {
              // Envoyer la notification push
              await pushNotificationService.sendPushNotification(
                notification.userId,
                {
                  title: notification.title,
                  body: notification.message,
                  data: {
                    notificationId: docRef.id,
                    category: notification.category,
                    link: notification.link,
                    importance: notification.importance
                  }
                }
              );
            } else {
              // La notification est déjà stockée dans Firestore et sera affichée via l'interface utilisateur
            }
          }
        }
      } catch (error) {
        console.error('[DEBUG] Error sending push notification:', error);
        // Ne pas échouer si l'envoi de la notification push échoue
        // La notification est déjà stockée dans Firestore
      }

      return docRef.id;
    } catch (error) {
      console.error('[DEBUG] Error creating notification:', error);
      throw error;
    }
  }

  async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, this.collectionName, notificationId);
      await updateDoc(notificationRef, { 
        isRead: true,
        read: true,
        status: 'clicked',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('[DEBUG] Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead(weddingId: string, category?: string): Promise<void> {
    try {
      const notificationsRef = collection(db, this.collectionName);
      let q = query(
        notificationsRef,
        where('weddingId', '==', weddingId),
        where('isRead', '==', false)
      );

      if (category) {
        q = query(q, where('category', '==', category));
      }

      const querySnapshot = await getDocs(q);
      
      await Promise.all(
        querySnapshot.docs.map(doc =>
          updateDoc(doc.ref, { 
            isRead: true,
            read: true,
            status: 'clicked',
            updatedAt: serverTimestamp()
          })
        )
      );
    } catch (error) {
      console.error('[DEBUG] Error marking all notifications as read:', error);
      throw error;
    }
  }

  getNotificationCount(notifications: Notification[]): NotificationCount {
    try {
      const counts = notifications.reduce((acc, notification) => {
        if (!notification.isRead) {
          const category = notification.category;
          acc[category] = (acc[category] || 0) + 1;
        }
        return acc;
      }, {} as NotificationCount);
      return counts;
    } catch (error) {
      console.error('[DEBUG] Error calculating notification counts:', error);
      return {};
    }
  }

  async enablePushNotifications(userId: string): Promise<boolean> {
    try {
      return pushNotificationService.setupPushNotifications()
        .then(enabled => {
          if (enabled) {
            return pushNotificationService.subscribeToPushNotifications(userId);
          }
          return false;
        });
    } catch (error) {
      console.error('[DEBUG] Error enabling push notifications:', error);
      return false;
    }
  }

  async disablePushNotifications(userId: string): Promise<boolean> {
    try {
      return pushNotificationService.unsubscribeFromPushNotifications(userId);
    } catch (error) {
      console.error('[DEBUG] Error disabling push notifications:', error);
      return false;
    }
  }

  async testNotification(userId: string): Promise<boolean> {
    try {
      
      // Récupérer le weddingId de l'utilisateur
      const userWeddingsQuery = query(
        collection(db, 'weddings'),
        where('userId', '==', userId)
      );
      
      const userWeddingsSnapshot = await getDocs(userWeddingsQuery);
      if (userWeddingsSnapshot.empty) {
        console.error('[DEBUG] No wedding found for user:', userId);
        return false;
      }
      
      const weddingId = userWeddingsSnapshot.docs[0].id;
      
      // Créer une notification de test
      await this.createNotification({
        weddingId,
        userId,
        title: 'Notification de test',
        message: 'Ceci est une notification de test pour vérifier que les notifications fonctionnent correctement.',
        type: 'info',
        category: 'general',
        importance: 'medium',
        link: '/settings/notifications'
      });
      
      return true;
    } catch (error) {
      console.error('[DEBUG] Error sending test notification:', error);
      return false;
    }
  }

  async checkPushEnabled(userId: string): Promise<boolean> {
    try {
      // Vérifier si les notifications push sont activées pour l'utilisateur
      const userPrefsQuery = query(
        collection(db, 'userPreferences'),
        where('userId', '==', userId)
      );
      
      const userPrefsSnapshot = await getDocs(userPrefsQuery);
      if (userPrefsSnapshot.empty) {
        return false;
      }
      
      const userPrefs = userPrefsSnapshot.docs[0].data();
      return userPrefs['pushNotificationsEnabled'] || false;
    } catch (error) {
      console.error('[DEBUG] Error checking push notifications status:', error);
      return false;
    }
  }

  async getNotificationPreferences(userId: string): Promise<any> {
    try {
      const userPrefsQuery = query(
        collection(db, 'userPreferences'),
        where('userId', '==', userId)
      );
      
      const userPrefsSnapshot = await getDocs(userPrefsQuery);
      if (userPrefsSnapshot.empty) {
        return {
          email: true,
          push: true,
          reminders: true
        };
      }
      
      const userPrefs = userPrefsSnapshot.docs[0].data();
      return {
        email: userPrefs['emailNotifications'] || true,
        push: userPrefs['pushNotificationsEnabled'] || true,
        reminders: userPrefs['reminderNotifications'] || true
      };
    } catch (error) {
      console.error('[DEBUG] Error getting notification preferences:', error);
      return {
        email: true,
        push: true,
        reminders: true
      };
    }
  }

  async updateNotificationPreferences(userId: string, preferences: any): Promise<void> {
    try {
      const userPrefsQuery = query(
        collection(db, 'userPreferences'),
        where('userId', '==', userId)
      );
      
      const userPrefsSnapshot = await getDocs(userPrefsQuery);
      
      if (userPrefsSnapshot.empty) {
        // Créer de nouvelles préférences
        await addDoc(collection(db, 'userPreferences'), {
          userId,
          emailNotifications: preferences.email,
          pushNotificationsEnabled: preferences.push,
          reminderNotifications: preferences.reminders,
          updatedAt: serverTimestamp()
        });
      } else {
        // Mettre à jour les préférences existantes
        const userPrefsDoc = userPrefsSnapshot.docs[0];
        await updateDoc(doc(db, 'userPreferences', userPrefsDoc.id), {
          emailNotifications: preferences.email,
          pushNotificationsEnabled: preferences.push,
          reminderNotifications: preferences.reminders,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('[DEBUG] Error updating notification preferences:', error);
      throw error;
    }
  }
}

export const notificationService = new NotificationService(); 